import apiClient, { API_ENDPOINTS } from './config';
import {
  AzureRepoIntegrationPayload,
  DomainFocusArea,
  CreateProjectPayload,
  DomainKnowledge,
  EnterpriseKnowledge,
  EnterpriseKnowledgePayload,
  KnowledgeDocument,
  ProjectAnalyticsResponse,
  ProjectDeletionImpact,
  ProjectWithRepository,
  RepositorySummary,
  UpdateProjectPayload,
} from '../types/project';

export const fetchProjects = async (): Promise<ProjectWithRepository[]> => {
  const response = await apiClient.get<any>(API_ENDPOINTS.PROJECTS);
  const data = response.data;
  return data?.items ?? (Array.isArray(data) ? data : []);
};

export interface ProjectsFilterParams
{
  search?: string;
  project_type?: string;
  organization_id?: number;
  date_from?: string;
  date_to?: string;
}

export interface PagedProjectsResponse
{
  items: ProjectWithRepository[];
  total: number;
  page: number;
  page_size: number;
}

export const fetchProjectsPaged = async (
  page: number,
  pageSize: number,
  filters?: ProjectsFilterParams,
): Promise<PagedProjectsResponse> => {
  const params: Record<string, string | number> = { page, page_size: pageSize };
  if (filters?.search) params.search = filters.search;
  if (filters?.project_type) params.project_type = filters.project_type;
  if (filters?.organization_id) params.organization_id = filters.organization_id;
  if (filters?.date_from) params.date_from = filters.date_from;
  if (filters?.date_to) params.date_to = filters.date_to;
  const response = await apiClient.get<PagedProjectsResponse>(API_ENDPOINTS.PROJECTS, { params });
  return response.data;
};

export const fetchProject = async (projectId: number | string): Promise<ProjectWithRepository> => {
  const response = await apiClient.get<ProjectWithRepository>(API_ENDPOINTS.PROJECT_DETAIL(projectId));
  return response.data;
};

export const fetchProjectAnalytics = async (
  projectId: number | string,
): Promise<ProjectAnalyticsResponse> => {
  const response = await apiClient.get<ProjectAnalyticsResponse>(API_ENDPOINTS.PROJECT_ANALYTICS(projectId));
  return response.data;
};

export const createProject = async (
  payload: CreateProjectPayload,
): Promise<ProjectWithRepository> => {
  const response = await apiClient.post<ProjectWithRepository>(API_ENDPOINTS.PROJECTS, payload);
  return response.data;
};

export interface CreateProjectWithKnowledgeOptions
{
  domainFiles?: File[];
  enterpriseKnowledge?: EnterpriseKnowledgePayload | null;
  enterpriseFiles?: File[];
}

export const createProjectWithKnowledge = async (
  payload: CreateProjectPayload,
  options: CreateProjectWithKnowledgeOptions = {},
): Promise<ProjectWithRepository> => {
  const formData = new FormData();
  formData.append('project_payload', JSON.stringify(payload));
  if (options.enterpriseKnowledge) {
    formData.append('enterprise_knowledge_payload', JSON.stringify(options.enterpriseKnowledge));
  }
  (options.domainFiles || []).forEach(file => formData.append('domain_files', file));
  (options.enterpriseFiles || []).forEach(file => formData.append('enterprise_files', file));

  const response = await apiClient.post<ProjectWithRepository>(
    API_ENDPOINTS.PROJECTS_WITH_KNOWLEDGE,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const updateProject = async (
  projectId: number | string,
  payload: UpdateProjectPayload,
): Promise<ProjectWithRepository> => {
  const response = await apiClient.put<ProjectWithRepository>(
    API_ENDPOINTS.PROJECT_DETAIL(projectId),
    payload,
  );
  return response.data;
};

export const deleteProject = async (projectId: number | string): Promise<void> => {
  await apiClient.delete(API_ENDPOINTS.PROJECT_DETAIL(projectId));
};

export const renameProject = async (
  projectId: number | string,
  name: string,
): Promise<ProjectWithRepository> => {
  const response = await apiClient.patch<ProjectWithRepository>(
    API_ENDPOINTS.PROJECT_RENAME(projectId),
    { name },
  );
  return response.data;
};

export const fetchProjectDeletionImpact = async (
  projectId: number | string,
): Promise<ProjectDeletionImpact> => {
  const response = await apiClient.get<ProjectDeletionImpact>(
    API_ENDPOINTS.PROJECT_DELETION_IMPACT(projectId),
  );
  return response.data;
};

export const uploadProjectRepository = async (
  projectId: number | string,
  file: File,
  metadata?: { name?: string; description?: string; azureRepoName?: string; azureBranch?: string },
): Promise<{ message: string; repository_id: number; project_id?: number; file_name: string; file_size: number }> => {
  const formData = new FormData();
  formData.append('file', file);
  if (metadata?.name) {
    formData.append('name', metadata.name);
  }
  if (metadata?.description) {
    formData.append('description', metadata.description);
  }
  if (metadata?.azureRepoName) {
    formData.append('repo_name', metadata.azureRepoName);
  }
  if (metadata?.azureBranch) {
    formData.append('branch', metadata.azureBranch);
  }

  const response = await apiClient.post(
    API_ENDPOINTS.PROJECT_REPOSITORY(projectId),
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );

  return response.data;
};

export const integrateAzureRepo = async (
  projectId: number | string,
  payload: AzureRepoIntegrationPayload,
): Promise<{
  message: string;
  repository_id: number;
  project_id: number;
  azure_repo_url: string;
  repository_name: string;
  branch: string;
  files_uploaded: number;
}> => {
  const response = await apiClient.post(
    API_ENDPOINTS.PROJECT_AZURE_REPO_INTEGRATION(projectId),
    {
      azure_repo_url: payload.azureRepoUrl,
      branch: payload.branch ?? 'main',
    },
  );

  return response.data;
};

export const fetchProjectRepository = async (
  projectId: number | string,
): Promise<RepositorySummary> => {
  const response = await apiClient.get<RepositorySummary>(API_ENDPOINTS.PROJECT_REPOSITORY(projectId));
  return response.data;
};

export const deleteProjectRepository = async (projectId: number | string): Promise<void> => {
  await apiClient.delete(API_ENDPOINTS.PROJECT_REPOSITORY(projectId));
};

export const fetchProjectDomainKnowledge = async (
  projectId: number | string,
): Promise<DomainKnowledge> => {
  const response = await apiClient.get<DomainKnowledge>(API_ENDPOINTS.PROJECT_DOMAIN_KNOWLEDGE(projectId));
  return response.data;
};

export const refreshProjectDomainKnowledge = async (
  projectId: number | string,
  payload?: { document_ids?: number[] },
): Promise<DomainKnowledge> => {
  const response = await apiClient.post<DomainKnowledge>(
    API_ENDPOINTS.PROJECT_DOMAIN_KNOWLEDGE_REFRESH(projectId),
    payload ?? {},
    {
      timeout: 100000,
    },
  );
  return response.data;
};

export const saveProjectDomainFocusAreas = async (
  projectId: number | string,
  focusAreas: DomainFocusArea[],
): Promise<DomainKnowledge> => {
  const response = await apiClient.put<DomainKnowledge>(
    API_ENDPOINTS.PROJECT_DOMAIN_KNOWLEDGE_FOCUS_AREAS(projectId),
    { focus_areas: focusAreas },
  );
  return response.data;
};

export const fetchProjectEnterpriseKnowledge = async (
  projectId: number | string,
): Promise<EnterpriseKnowledge> => {
  const response = await apiClient.get<EnterpriseKnowledge>(
    API_ENDPOINTS.PROJECT_ENTERPRISE_KNOWLEDGE(projectId),
  );
  return response.data;
};

export const saveProjectEnterpriseKnowledge = async (
  projectId: number | string,
  payload: EnterpriseKnowledgePayload,
): Promise<EnterpriseKnowledge> => {
  const response = await apiClient.put<EnterpriseKnowledge>(
    API_ENDPOINTS.PROJECT_ENTERPRISE_KNOWLEDGE(projectId),
    payload,
  );
  return response.data;
};

export const refreshProjectEnterpriseKnowledge = async (
  projectId: number | string,
  payload?: { document_ids?: number[] },
): Promise<EnterpriseKnowledge> => {
  const response = await apiClient.post<EnterpriseKnowledge>(
    API_ENDPOINTS.PROJECT_ENTERPRISE_KNOWLEDGE_REFRESH(projectId),
    payload ?? {},
    { timeout: 100000 },
  );
  return response.data;
};

export const uploadKnowledgeDocuments = async (
  projectId: number | string,
  files: File[],
  knowledgeType: 'domain' | 'enterprise',
): Promise<KnowledgeDocument[]> => {
  const formData = new FormData();
  formData.append('knowledge_type', knowledgeType);
  files.forEach(file => formData.append('files', file));
  const response = await apiClient.post<KnowledgeDocument[]>(
    `/api/projects/${projectId}/knowledge/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const fetchKnowledgeDocuments = async (
  projectId: number | string,
  knowledgeType?: 'domain' | 'enterprise',
): Promise<KnowledgeDocument[]> => {
  const params = knowledgeType ? { knowledge_type: knowledgeType } : {};
  const response = await apiClient.get<KnowledgeDocument[]>(
    `/api/projects/${projectId}/knowledge/documents`,
    { params },
  );
  return response.data || [];
};

export const deleteKnowledgeDocument = async (
  projectId: number | string,
  documentId: number | string,
): Promise<void> => {
  await apiClient.delete(`/api/projects/${projectId}/knowledge/documents/${documentId}`);
};
